import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Table,
  Button,
  Badge,
  Spinner,
} from "reactstrap";

import gsap from "gsap";
import { toast } from "react-toastify";
import axiosInstance from "../../helpers/axiosInstance";

const WeightIssue = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const observerRef = useRef(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [productUpdates, setProductUpdates] = useState({});

  useEffect(() => {
    fetchProducts(1, searchTerm);
    fetchCategories();
  }, []);

  const fetchProducts = async (page = 1, name = "") => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `/api/v1/admin/products-without-volumetric-weight`
      );
      if(page === 1){
        setProducts(response.data.products);
      }else{
        setProducts((prev) => [...prev, ...response.data.products]);
      }
      setTotalProducts(response.data.total);
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load products. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get("api/v1/categories");
      const categoryMap = {};
      response.data.categories.forEach((cat) => {
        categoryMap[cat._id] = cat.name;
      });
      setCategories(categoryMap);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleObserver = useCallback(
    (entries) => {
      const target = entries[0];
      if (target.isIntersecting && products.length < totalProducts) {
        fetchProducts(currentPage + 1, searchTerm);
      }
    },
    [products, totalProducts, currentPage]
  );

  const handleDelete = async (productId) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        setLoading(true);
        await axiosInstance.delete(`api/v1/admin/product/${productId}`);
        toast.success("Product deleted successfully");
        setProducts((prevProducts) => prevProducts.filter(product => product._id !== productId));
      } catch (error) {
        console.error("Error deleting product:", error);
        alert(error.response?.data?.message || "Error deleting product");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProducts.length === 0) {
      alert("Please select at least one product to delete.");
      return;
    }

    if (window.confirm("Are you sure you want to delete the selected products?")) {
      try {
        setLoading(true);
        await axiosInstance.post("api/v1/admin/products/delete", {
          productIds: selectedProducts,
        });
        setProducts((prevProducts) =>
          prevProducts.filter(product => !selectedProducts.includes(product._id))
        );
        toast.success("Product deleted successfully");
        setSelectedProducts([]);
        setShowBulkDelete(false);

      } catch (error) {
        console.error("Error deleting products:", error);
        alert(error.response?.data?.message || "Error deleting products");
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (observerRef.current) {
      let observer = new IntersectionObserver(handleObserver, {
        root: null,
        rootMargin: "20px",
        threshold: 1.0,
      });
      observer.observe(observerRef.current);
      return () => observer.disconnect();
    }
  }, [products]);

  const handleCheckboxChange = (productId) => {
    let updatedSelectedProducts;
    if (selectedProducts.includes(productId)) {
      updatedSelectedProducts = selectedProducts.filter(
        (id) => id !== productId
      );
    } else {
      updatedSelectedProducts = [...selectedProducts, productId];
    }
    setSelectedProducts(updatedSelectedProducts);
    setShowBulkDelete(updatedSelectedProducts.length > 0);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(price);
  };

  const getDiscountedPrice = (price, discount) => {
    const discountedPrice = price - (price * discount) / 100;
    return formatPrice(discountedPrice);
  };

  const handleSearchSubmit = () => {
    setProducts([]);
    if(currentPage !== 1){
      setCurrentPage(1);
      fetchProducts(1, searchTerm);
    }else{
      setCurrentPage(1);
      fetchProducts(1, searchTerm);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleInputChange = (productId, field, value) => {
    setProductUpdates(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [field]: value
      }
    }));
  };

  const handleSave = async (productId) => {
    try {
      const updates = productUpdates[productId];
      if (!updates) return;

      setLoading(true);
      await axiosInstance.put(`api/v1/admin/product/${productId}`, {
        volumetricWeight: updates.weight || 0,
        hsn: updates.hsn || "",
        tax: updates.tax || 0
      });

      setProducts(prevProducts => 
        prevProducts.map(product => 
          product._id === productId 
            ? {
                ...product,
                volumetricWeight: updates.weight || 0,
                hsn: updates.hsn || "",
                tax: updates.tax || 0
              }
            : product
        )
      );

      setProductUpdates(prev => {
        const newUpdates = { ...prev };
        delete newUpdates[productId];
        return newUpdates;
      });

      toast.success("Product updated successfully");
    } catch (error) {
      console.error("Error updating product:", error);
      toast.error(error.response?.data?.message || "Error updating product");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <CardTitle className="h4">Products List</CardTitle>
                  <div className="d-flex gap-2">
                    
                    {showBulkDelete && (
                      <Button
                        color="danger"
                        onClick={handleBulkDelete}
                        className="me-2"
                      >
                        Bulk Delete
                      </Button>
                    )}
                    <Link to="/ecommerce-add-product">
                      <Button color="primary">Add New Product</Button>
                    </Link>
                  </div>
                </div>


                <div className="table-responsive">
                  <Table className="table-centered table-nowrap mb-0">
                    <thead className="table-light">
                      <tr>
                        
                        <th>Product</th>
                        <th>Weight</th>
                        <th>HSN CODE</th>
                        <th>TAX</th>
                        <th>Action</th>
                        <th>Price</th>
                       
                       
                       
                      
                       
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((product, index) => (
                        <tr
                          key={product._id}
                          className="product-row"
                          style={{
                            opacity: 0,
                            transform: "translateY(20px)",
                          }}
                          ref={(el) => {
                            if (el) {
                              gsap.to(el, {
                                opacity: 1,
                                y: 0,
                                duration: 0.1,
                                delay: index * 0.01,
                              });
                            }
                          }}
                        >
                         
                          
                          <td>
                            <div className="d-flex align-items-center text-center justify-center">
                              <img
                                src={product.images[0].startsWith("https") ? product.images[0] : `${process.env.REACT_APP_BASEURL}${product.images[0]}`}
                                alt={product.name}
                                className="rounded me-3"
                                height="64"
                                width="60"
                              />
                              <div>
                                <h5 style={{
                                    textOverflow:"ellipsis",
                                    overflow:"hidden",
                                    maxWidth:"200px"
                                  }} className="font-size-14 mb-1">
                                  <Link
                                  
                                    to={`/ecommerce-product-detail/${product._id}`}
                                    className="text-dark max-w-[200px]  overflow-hidden"
                                    target="_blank"
                                  >
                                    {product.name}
                                  </Link>
                                </h5>
                                
                              </div>
                            </div>
                          </td>
                          <td>
                            <div>
                              <input 
                                className="px-2 py-1" 
                                placeholder="weight (KG)" 
                                type="number"
                                step="0.1"
                                min="0"
                                value={productUpdates[product._id]?.weight || product.volumetricWeight || ''}
                                onChange={(e) => handleInputChange(product._id, 'weight', e.target.value)}
                              />
                            </div>
                          </td>

                          <td>
                            <div>
                              <input 
                                className="px-2 py-1" 
                                placeholder="HSN CODE" 
                                type="text"
                                value={productUpdates[product._id]?.hsn || product.hsn || ''}
                                onChange={(e) => handleInputChange(product._id, 'hsn', e.target.value)}
                              />
                            </div>
                          </td>

                          <td>
                            <div>
                              <input 
                                className="px-2 py-1" 
                                placeholder="TAX" 
                                type="number"
                                min="0"
                                max="100"
                                value={productUpdates[product._id]?.tax || product.tax || ''}
                                onChange={(e) => handleInputChange(product._id, 'tax', e.target.value)}
                              />
                            </div>
                          </td>
                          <td>
                            <button 
                              style={{
                                background: productUpdates[product._id] ? "#228B22" : "#cccccc",
                                border: "none", 
                                padding: "5px 15px", 
                                borderRadius: "6px",
                                cursor: productUpdates[product._id] ? "pointer" : "default"
                              }} 
                              className="text-white"
                              onClick={() => handleSave(product._id)}
                              disabled={!productUpdates[product._id]}
                            >
                              Save
                            </button>
                          </td>
                         
                          <td>
                            <div>
                              <h5 className="font-size-14 mb-1">
                                {getDiscountedPrice(
                                  product.price,
                                  product.discount
                                )}
                              </h5>
                              {product.discount > 0 && (
                                <div>
                                  <del className="text-muted me-1">
                                    {formatPrice(product.price)}
                                  </del>
                                  <Badge color="success">
                                    {product.discount}% off
                                  </Badge>
                                </div>
                              )}
                            </div>
                          </td>
                         
                         
                         
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  {products.length === 0 && !loading && (
                    <div className="text-center my-5">
                      <h4>No products found</h4>
                      <Link to="/ecommerce-add-product">
                        <Button color="primary" className="mt-2">
                          Add Your First Product
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>


                {/* Conditionally render pagination */}
                {/* {!showBulkDelete && (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px' }}>
                    <div className="flex justify-center my-4 align-items-center gap-2">
                      <button
                        className={`bg-primary text-white font-semibold py-2 px-4 rounded-l ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                      >
                        Previous
                      </button>
                      <span className="flex items-center mx-4 text-lg">Page {currentPage}</span>
                      <button
                        className={`bg-primary text-white font-semibold py-2 px-4 rounded-r ${currentPage * 10 >= totalProducts ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={currentPage * 10 >= totalProducts}
                        onClick={() => handlePageChange(currentPage + 1)}
                      >
                        Next
                      </button>
                    </div>
                  
                  </div>
                )} */}


              </CardBody>
            </Card>
          </Col>
        </Row>

        {loading && (
          <div className="text-center my-4">
            <Spinner color="primary" />
          </div>
        )}
        <div
          ref={observerRef}
          style={{ height: "10px", marginBottom: "20px" }}
        ></div>
      </Container>

    </div>
  );
};

export default WeightIssue;
